import React from "react";
import { Link } from "react-router-dom";
import QuoteForm from "@/components/QuoteForm";
import { SERVICES } from "@/lib/api";
import { useRegion, priceFor } from "@/contexts/RegionContext";
import {
  Phone,
  Shield,
  Clock,
  MapPin,
  DollarSign,
  Star,
  ArrowRight,
  Sofa,
  Trees,
  HardHat,
  PackageOpen,
  Refrigerator,
  DoorOpen,
  CloudLightning,
  Container,
} from "lucide-react";

const ICONS = { Sofa, Trees, HardHat, PackageOpen, Refrigerator, DoorOpen, CloudLightning, Container };

const HERO_BG =
  "https://images.unsplash.com/photo-1774013603273-03507c48a0e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxnYXJiYWdlJTIwdHJ1Y2slMjBydXJhbCUyMHJvYWR8ZW58MHx8fHwxNzgzNDQ4MDk0fDA&ixlib=rb-4.1.0&q=85";

const TRUST = [
  { icon: Shield, label: "Local Crews", note: "Trusted haulers nearby" },
  { icon: Clock, label: "Same-Day Availability", note: "Usually within hours" },
  { icon: DollarSign, label: "Upfront Pricing", note: "No surprise fees" },
  { icon: MapPin, label: "Serving Your Area", note: "Local coverage" },
];

const Home = () => {
  const { region } = useRegion();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[hsl(60,6%,11%)] text-white">
        <div
          className="absolute inset-0 opacity-40"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(60,6%,11%)] via-[hsl(60,6%,11%)]/70 to-transparent" />
        <div className="container-wide relative py-20 lg:py-28">
          <div className="grid gap-10 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <p className="kicker text-primary">Junk Removal · Trash Hauling · Same-Day When Possible</p>
              <h1 className="mt-4 font-display text-4xl font-black leading-[0.95] sm:text-5xl lg:text-6xl">
                Fast Junk Removal &amp; <br className="hidden sm:block" />
                <span className="text-primary">Trash Hauling Near You.</span>
              </h1>
              <p className="mt-5 max-w-xl text-lg text-white/80">
                Send photos, get a quick quote, and we&apos;ll match your pickup with an available local hauling crew.
                Photo quotes · Upfront pricing · No hidden fees.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a href="#quote" className="btn-cta" data-testid="hero-quote-cta">
                  Get a Free Hauling Quote <ArrowRight className="h-4 w-4" />
                </a>
                <a href="tel:+18135550100" className="btn-ghost-dark" data-testid="hero-call-cta">
                  <Phone className="h-4 w-4" /> Call for Same-Day Pickup
                </a>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {TRUST.map((t) => (
                  <div key={t.label} className="rounded-sm border border-white/15 bg-white/5 p-4">
                    <t.icon className="h-5 w-5 text-primary" />
                    <p className="mt-2 text-sm font-semibold">{t.label}</p>
                    <p className="text-xs text-white/60">{t.note}</p>
                  </div>
                ))}
              </div>
            </div>

            <div id="quote" className="lg:col-span-2">
              <QuoteForm variant="light" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="section">
        <div className="container-wide">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <p className="kicker">What we haul</p>
              <h2 className="mt-2 font-display text-3xl font-black sm:text-4xl lg:text-5xl">
                One call. Every kind of junk.
              </h2>
            </div>
            <Link to="/services" className="text-sm font-semibold text-primary hover:underline" data-testid="see-all-services-link">
              See all services →
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-sm bg-border sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || Sofa;
              return (
                <Link
                  key={s.slug}
                  to={`/services#${s.slug}`}
                  className="group flex flex-col gap-3 bg-card p-6 transition-colors hover:bg-muted"
                  data-testid={`home-service-${s.slug}`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-secondary/10 text-secondary">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg font-bold">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.short}</p>
                  <p className="mt-auto text-sm font-semibold text-primary">
                    From ${priceFor(s, region)} · Free quote →
                  </p>
                </Link>
              );
            })}
          </div>
          <div className="mt-6">
            <p className="max-w-2xl text-xs text-muted-foreground">
              Prices start at the amounts shown. Final pricing depends on load size, material, stairs, distance, urgency, and disposal. Send photos for an exact quote.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section bg-muted grid-lines-bg">
        <div className="container-wide">
          <p className="kicker">How it works</p>
          <h2 className="mt-2 font-display text-3xl font-black sm:text-4xl lg:text-5xl">
            Three steps. Zero hassle.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Request", d: "Tell us what you need hauled — takes about 60 seconds." },
              { n: "02", t: "Schedule", d: "We call you back with a firm, upfront price and a pickup window." },
              { n: "03", t: "Done", d: "Our crew shows up, loads, hauls, and disposes. You pay only what was quoted." },
            ].map((step) => (
              <div key={step.n} className="border-l-4 border-primary bg-card p-6" data-testid={`step-${step.n}`}>
                <p className="font-display text-3xl font-black text-primary">{step.n}</p>
                <h3 className="mt-2 font-display text-xl font-bold">{step.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section">
        <div className="container-wide">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <p className="kicker">Why homeowners call us</p>
              <h2 className="mt-2 font-display text-3xl font-black sm:text-4xl lg:text-5xl">
                Real crews. Real trucks. Real quick.
              </h2>
              <p className="mt-4 text-muted-foreground">
                We keep it simple: honest upfront pricing, dependable local crews, and same-day pickup on most
                jobs. From a single fridge in the garage to a full property cleanout, we haul it and dispose of it
                the right way.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  "No hidden fees",
                  "Same-day service",
                  "Local crews",
                  "Eco-friendly disposal",
                ].map((v) => (
                  <div key={v} className="rounded-sm border border-border bg-card px-4 py-3 text-sm font-semibold">
                    ✓ {v}
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-sm border border-border bg-[hsl(145,27%,24%)] p-8 text-white">
              <Star className="h-6 w-6 text-primary" />
              <p className="mt-4 font-display text-xl font-bold leading-snug">
                &ldquo;Called on a Saturday morning — the crew was in my driveway before noon and the whole storm mess
                in my backyard was gone by 3.&rdquo;
              </p>
              <p className="mt-4 text-sm text-white/70">— Dana R., Homeowner</p>
              <Link to="/contact" className="btn-cta mt-6" data-testid="testimonial-cta">
                Book Your Pickup <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
